/**
 * La campanella: dove arrivano gli avvisi di Otto («Salvato», «Annullato»,
 * «Non sono riuscito a…»).
 *
 * Prima uscivano come una pillola in basso al centro, sopra la casella della
 * chat: coprivano proprio quello che si stava scrivendo, e dopo quattro secondi
 * sparivano senza lasciare traccia — chi guardava altrove non sapeva nemmeno
 * che fosse successo qualcosa. Adesso:
 *
 *  - l'avviso esce **dalla campanella**, in alto a destra, come un ovale con
 *    il testo, e dopo qualche secondo rientra;
 *  - la campanella tiene il conto di quelli non visti;
 *  - toccandola si aprono tutti, dal più recente, uno sotto l'altro.
 *
 * L'ovale sta in un portale sopra tutto: un avviso nato dentro una finestra
 * (il calendario di un immobile, per esempio) si deve vedere anche se la
 * finestra copre la campanella.
 */

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Bell, BellOff } from "lucide-react";
import { useStore, type Notifica } from "../lib/store.tsx";
import { cx } from "./primitives.tsx";

export function Campanella() {
  const store = useStore();
  const [aperta, setAperta] = useState(false);
  const bottone = useRef<HTMLButtonElement | null>(null);
  const pannello = useRef<HTMLDivElement | null>(null);
  const [punto, setPunto] = useState<{ top: number; right: number } | null>(
    null,
  );

  // L'ovale esce accanto alla campanella, ovunque sia finita nella pagina.
  useLayoutEffect(() => {
    if (!store.toast) return;
    const r = bottone.current?.getBoundingClientRect();
    if (r) {
      setPunto({
        top: r.top + r.height / 2,
        right: window.innerWidth - r.left + 8,
      });
    }
  }, [store.toast]);

  // Si chiude toccando fuori o con Esc, come ogni menu.
  useEffect(() => {
    if (!aperta) return;
    const fuori = (event: MouseEvent) => {
      const bersaglio = event.target as Node;
      if (
        !pannello.current?.contains(bersaglio) &&
        !bottone.current?.contains(bersaglio)
      ) {
        setAperta(false);
      }
    };
    const tasto = (event: KeyboardEvent) => {
      if (event.key === "Escape") setAperta(false);
    };
    document.addEventListener("mousedown", fuori);
    document.addEventListener("keydown", tasto);
    return () => {
      document.removeEventListener("mousedown", fuori);
      document.removeEventListener("keydown", tasto);
    };
  }, [aperta]);

  const apri = () => {
    const prossimo = !aperta;
    setAperta(prossimo);
    if (prossimo) store.segnaLette();
  };

  const nonLette = store.nonLette;

  return (
    <div className="relative shrink-0">
      <button
        ref={bottone}
        type="button"
        onClick={apri}
        aria-label={nonLette > 0 ? `Notifiche, ${nonLette} nuove` : "Notifiche"}
        aria-expanded={aperta}
        className={cx(
          "relative grid size-10 place-items-center rounded-full transition-all duration-quick active:scale-95",
          aperta
            ? "bg-ink text-ink-inverse"
            : "bg-surface-muted text-ink hover:bg-surface-sunken",
        )}
      >
        <Bell size={18} />
        {nonLette > 0 ? (
          <span className="animate-pop absolute -right-0.5 -top-0.5 grid h-[18px] min-w-[18px] place-items-center rounded-full bg-danger px-1 text-[10px] font-bold tabular-nums text-ink-inverse ring-2 ring-surface">
            {nonLette > 9 ? "9+" : nonLette}
          </span>
        ) : null}
      </button>

      {aperta ? (
        <div
          ref={pannello}
          role="dialog"
          aria-label="Notifiche"
          className="animate-pop absolute right-0 top-full z-40 mt-2 w-[min(360px,calc(100vw-32px))] origin-top-right overflow-hidden rounded-card-lg border border-line bg-surface shadow-lift"
        >
          <div className="flex items-center justify-between border-b border-line px-4 py-3">
            <p className="text-[14px] font-extrabold text-ink">Notifiche</p>
            {store.notifiche.length > 0 ? (
              <button
                type="button"
                onClick={store.svuotaNotifiche}
                className="text-[12.5px] font-semibold text-ink-soft hover:text-ink"
              >
                Svuota
              </button>
            ) : null}
          </div>
          {store.notifiche.length === 0 ? (
            <div className="flex flex-col items-center gap-2 px-6 py-10 text-center">
              <span className="grid size-10 place-items-center rounded-full bg-surface-muted text-ink-faint">
                <BellOff size={17} />
              </span>
              <p className="text-[13px] text-ink-soft">
                Niente di nuovo. Quando Otto ti dice qualcosa, lo ritrovi qui.
              </p>
            </div>
          ) : (
            <ul className="scroll-soft flex max-h-[min(420px,70vh)] flex-col gap-1.5 overflow-y-auto p-2">
              {store.notifiche.map((n, i) => (
                <RigaNotifica
                  key={n.id}
                  notifica={n}
                  indice={i}
                  onAzione={() => {
                    n.azione?.esegui();
                    setAperta(false);
                  }}
                />
              ))}
            </ul>
          )}
        </div>
      ) : null}

      {store.toast && punto && !aperta
        ? createPortal(
            // Due livelli: quello fuori si centra sulla campanella, quello
            // dentro si anima. Sullo stesso elemento l'animazione riscriveva
            // lo spostamento, e l'ovale nasceva mezzo sotto.
            <div
              className="pointer-events-none fixed z-[70]"
              style={{
                top: punto.top,
                right: punto.right,
                transform: "translateY(-50%)",
              }}
            >
              <div
                key={store.notificaInVista?.id}
                role="status"
                aria-live="polite"
                className="animate-ovale flex min-h-10 max-w-[min(420px,calc(100vw-80px))] origin-right items-center gap-3 rounded-[20px] bg-ink py-2 pl-4 pr-2 text-[13px] font-semibold leading-snug text-ink-inverse shadow-lift"
              >
                <span className="py-0.5">{store.toast}</span>
                {store.notificaInVista?.azione ? (
                  // Solo il tasto riceve i clic: l'ovale resta trasparente al
                  // mouse, per non coprire quello che c'è sotto.
                  <button
                    type="button"
                    onClick={() => {
                      store.notificaInVista?.azione?.esegui();
                      store.segnaLette();
                    }}
                    className="pointer-events-auto shrink-0 rounded-pill bg-ink-inverse px-3 py-1 text-[12.5px] font-bold text-ink transition-transform duration-quick active:scale-95"
                  >
                    {store.notificaInVista.azione.etichetta}
                  </button>
                ) : (
                  <span className="w-2" />
                )}
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

function RigaNotifica({
  notifica,
  indice,
  onAzione,
}: {
  notifica: Notifica;
  indice: number;
  onAzione: () => void;
}) {
  return (
    <li
      className="animate-cascata flex items-center gap-3 rounded-card bg-surface-muted px-3.5 py-2.5"
      // A cascata: una dopo l'altra, ma senza far aspettare chi ne ha trenta.
      style={{ animationDelay: `${Math.min(indice, 8) * 40}ms` }}
    >
      <div className="min-w-0 flex-1">
        <p className="text-[13px] leading-snug text-ink">{notifica.testo}</p>
        <p className="mt-0.5 text-[11.5px] text-ink-faint">
          {quandoLeggibile(notifica.quando, Date.now())}
        </p>
      </div>
      {notifica.azione ? (
        <button
          type="button"
          onClick={onAzione}
          className="shrink-0 rounded-pill bg-ink px-3 py-1.5 text-[12.5px] font-bold text-ink-inverse transition-transform duration-quick active:scale-95"
        >
          {notifica.azione.etichetta}
        </button>
      ) : null}
    </li>
  );
}

/** «adesso», «4 min fa», o l'ora: una sessione dura al massimo una giornata. */
export function quandoLeggibile(quando: number, adesso: number): string {
  const minuti = Math.floor((adesso - quando) / 60_000);
  if (minuti < 1) return "adesso";
  if (minuti < 60) return `${minuti} min fa`;
  return new Date(quando).toLocaleTimeString("it-IT", {
    hour: "2-digit",
    minute: "2-digit",
  });
}
